import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import * as SubframeCore from "@subframe/core";
import { FeatherChevronDown, FeatherSearch, FeatherX } from "@subframe/core";
import { Accordion } from "../../../ui/components/Accordion";
import { TextField } from "../../../ui/components/TextField";
import { Loader } from "../../../ui/components/Loader";
import { Button } from "../../../ui/components/Button";
import { getAccountNameLeaf } from "../../../utils";
import {
  AccountTreeGroup,
  AccountTreeNode,
} from "../../domain/entities/AccountTreeEntity";
import { GLMappingEntity } from "../../domain/entities/GLMappingEntity";
import {
  buildPathFromTree,
  buildSelectedAccountDisplayPath,
  filterExcludedTreeNodes,
  findNodeByAccountId,
  formatAccountTypeLabel,
  getTreeRowPaddingPx,
  hasSelectedDescendant,
  orderGroupsWithSelectedFirst,
  treeNodeToGLMappingEntity,
} from "../../utils/glTreeUtils";
import { UseBankGlTreeReturn } from "../hooks/useBankGlTree";
import { GlTreePickerHookState } from "../types/glTreePickerTypes";

export type { GlTreePickerHookState };

function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <div className="flex items-center justify-center rounded-full bg-neutral-100 px-2 py-0.5">
      <span className="text-caption font-caption text-subtext-color">
        {count}
      </span>
    </div>
  );
}

function scrollAccordionIntoView(
  accordionEl: HTMLElement,
  scrollContainer: HTMLElement,
) {
  const scroll = () => {
    const containerRect = scrollContainer.getBoundingClientRect();
    const accordionRect = accordionEl.getBoundingClientRect();
    const accordionBottom =
      accordionRect.bottom - containerRect.top + scrollContainer.scrollTop;
    const visibleBottom =
      scrollContainer.scrollTop + scrollContainer.clientHeight;
    if (accordionBottom > visibleBottom) {
      scrollContainer.scrollTo({
        top: accordionBottom - scrollContainer.clientHeight + 8,
        behavior: "smooth",
      });
    }
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(scroll);
  });
}

function useAccordionScrollOnOpen(
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
) {
  const accordionRef = useRef<HTMLDivElement>(null);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open && accordionRef.current && scrollContainerRef.current) {
        scrollAccordionIntoView(
          accordionRef.current,
          scrollContainerRef.current,
        );
      }
    },
    [scrollContainerRef],
  );

  return { accordionRef, onOpenChange };
}

interface CurrentlySelectedAccountRowProps {
  accountId: string;
  accountName: string;
  locationPath?: string | null;
  onSelect: (account: GLMappingEntity) => void;
}

function CurrentlySelectedAccountRow({
  accountId,
  accountName,
  locationPath,
  onSelect,
}: CurrentlySelectedAccountRowProps) {
  return (
    <div className="flex w-full flex-none flex-col items-start gap-1 border-b border-solid border-neutral-border px-2 pb-2 mb-1">
      <span className="text-caption font-caption text-subtext-color px-1">
        Currently selected
      </span>
      <button
        type="button"
        className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left bg-[#dbeafe] hover:bg-[#bfdbfe]"
        onClick={() =>
          onSelect({
            account_id: accountId,
            gl_id: "",
            gl_name: accountName,
            is_active: "1",
            created_at: new Date().toISOString(),
          })
        }
      >
        <span className="text-body font-body text-[#1d4ed8]">
          {accountName}
        </span>
        {locationPath && (
          <span className="text-caption font-caption text-[#1d4ed8]/80 truncate w-full">
            {locationPath}
          </span>
        )}
      </button>
    </div>
  );
}

interface AccountTypeGroupAccordionProps {
  group: AccountTreeGroup;
  searchActive: boolean;
  selectedAccountId?: string;
  isTypeLoading: boolean;
  onExpandType: (accountType: string) => void;
  onSelect: (account: GLMappingEntity) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  selectionMode?: "leaf" | "any";
  excludeAccountIds?: ReadonlySet<string>;
  defaultOpen?: boolean;
}

function AccountTypeGroupAccordion({
  group,
  searchActive,
  selectedAccountId,
  isTypeLoading,
  onExpandType,
  onSelect,
  scrollContainerRef,
  selectionMode = "leaf",
  excludeAccountIds,
  defaultOpen = false,
}: AccountTypeGroupAccordionProps) {
  const { accordionRef, onOpenChange: onScrollOpenChange } =
    useAccordionScrollOnOpen(scrollContainerRef);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onScrollOpenChange(open);
      if (open && !searchActive) {
        onExpandType(group.account_type);
      }
    },
    [group.account_type, onExpandType, onScrollOpenChange, searchActive],
  );

  return (
    <Accordion
      ref={accordionRef}
      defaultOpen={searchActive || defaultOpen}
      onOpenChange={handleOpenChange}
      trigger={
        <div className="flex w-full items-center justify-between rounded-md px-2 py-2 gap-2 hover:bg-neutral-100">
          <div className="flex items-center gap-2 min-w-0">
            <Accordion.Chevron className="text-subtext-color flex-none" />
            <span className="text-body-bold font-body-bold text-default-font truncate">
              {group.label}
            </span>
          </div>
          <CountBadge count={group.total_leaves} />
        </div>
      }
    >
      <div className="flex w-full flex-col items-start">
        {isTypeLoading ? (
          <div className="flex items-center justify-center py-4 w-full">
            <Loader />
          </div>
        ) : group.nodes.length === 0 && !searchActive ? (
          <div className="px-3 py-2 text-caption font-caption text-subtext-color w-full">
            Expand to load accounts
          </div>
        ) : (
          filterExcludedTreeNodes(group.nodes, excludeAccountIds).map(
            (node) => (
              <TreeNodeRow
                key={node.account_id}
                node={node}
                depth={0}
                selectedAccountId={selectedAccountId}
                searchActive={searchActive}
                onSelect={onSelect}
                scrollContainerRef={scrollContainerRef}
                selectionMode={selectionMode}
                excludeAccountIds={excludeAccountIds}
              />
            ),
          )
        )}
      </div>
    </Accordion>
  );
}

interface TreeNodeRowProps {
  node: AccountTreeNode;
  depth: number;
  selectedAccountId?: string;
  searchActive: boolean;
  onSelect: (account: GLMappingEntity) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  selectionMode?: "leaf" | "any";
  excludeAccountIds?: ReadonlySet<string>;
}

function TreeNodeRow({
  node,
  depth,
  selectedAccountId,
  searchActive,
  onSelect,
  scrollContainerRef,
  selectionMode = "leaf",
  excludeAccountIds,
}: TreeNodeRowProps) {
  const { accordionRef, onOpenChange } =
    useAccordionScrollOnOpen(scrollContainerRef);
  const isSelected = !searchActive && selectedAccountId === node.account_id;
  const shouldExpand =
    searchActive || hasSelectedDescendant(node, selectedAccountId);
  const visibleChildren = filterExcludedTreeNodes(
    node.children ?? [],
    excludeAccountIds,
  );

  const handleSelectParent = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (node.is_selectable) {
        onSelect(treeNodeToGLMappingEntity(node));
      }
    },
    [node, onSelect],
  );

  if (node.has_children && (node.children?.length ?? 0) > 0) {
    return (
      <Accordion
        ref={accordionRef}
        defaultOpen={shouldExpand}
        onOpenChange={onOpenChange}
        trigger={
          <div
            className={`flex w-full items-center justify-between rounded-md pr-2 py-1.5 gap-2 hover:bg-neutral-100 ${
              isSelected ? "bg-[#dbeafe]" : ""
            }`}
            style={{ paddingLeft: getTreeRowPaddingPx(depth) }}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Accordion.Chevron className="text-subtext-color flex-none" />
              {node.is_selectable ? (
                <button
                  type="button"
                  className={`text-body font-body truncate text-left min-w-0 ${
                    isSelected
                      ? "text-[#1d4ed8]"
                      : depth === 0
                        ? "text-default-font font-body-bold hover:text-[#1d4ed8]"
                        : "text-default-font hover:text-[#1d4ed8]"
                  }`}
                  onClick={handleSelectParent}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  {node.gl_name}
                </button>
              ) : (
                <span
                  className={`text-body font-body truncate ${
                    depth === 0
                      ? "text-default-font font-body-bold"
                      : "text-default-font"
                  }`}
                >
                  {node.gl_name}
                </span>
              )}
            </div>
            <CountBadge count={node.leaf_count} />
          </div>
        }
      >
        <div className="flex w-full flex-col items-start">
          {visibleChildren.map((child) => (
            <TreeNodeRow
              key={child.account_id}
              node={child}
              depth={depth + 1}
              selectedAccountId={selectedAccountId}
              searchActive={searchActive}
              onSelect={onSelect}
              scrollContainerRef={scrollContainerRef}
              selectionMode={selectionMode}
              excludeAccountIds={excludeAccountIds}
            />
          ))}
        </div>
      </Accordion>
    );
  }

  if (!node.is_selectable) {
    return null;
  }

  return (
    <button
      type="button"
      className={`flex w-full items-center gap-2 rounded-md pr-2 py-1.5 text-left ${
        isSelected ? "bg-[#dbeafe]" : "hover:bg-neutral-100"
      }`}
      style={{ paddingLeft: getTreeRowPaddingPx(depth) }}
      onClick={() => onSelect(treeNodeToGLMappingEntity(node))}
    >
      <span
        className={`text-body font-body ${
          isSelected
            ? "text-[#1d4ed8]"
            : "text-subtext-color hover:text-default-font"
        }`}
      >
        {node.gl_name}
      </span>
    </button>
  );
}

export interface GlTreePickerShellProps {
  treeState: GlTreePickerHookState;
  selectedAccountId?: string;
  selectedAccountName?: string;
  onSelect: (account: GLMappingEntity) => void;
  disabled?: boolean;
  className?: string;
  dropdownClassName?: string;
  placeholder?: string;
  buttonClassName?: string;
  onOpenChange?: (open: boolean) => void;
  selectionMode?: "leaf" | "any";
  excludeAccountIds?: ReadonlySet<string>;
  singleAccountType?: string;
  autoExpandSingleGroup?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  noResultsMessage?: string;
}

export function GlTreePickerShell({
  treeState,
  selectedAccountId,
  selectedAccountName,
  onSelect,
  disabled = false,
  className = "",
  dropdownClassName = "",
  placeholder = "Select an account",
  buttonClassName = "",
  onOpenChange,
  selectionMode = "leaf",
  excludeAccountIds,
  singleAccountType,
  autoExpandSingleGroup = false,
  searchPlaceholder = "Search accounts…",
  emptyMessage = "No accounts available",
  noResultsMessage = "No accounts match your search",
}: GlTreePickerShellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const {
    groups,
    loading,
    loadingMore,
    loadingTypes,
    hasMore,
    searchTerm,
    searchActive,
    selectedAccountType,
    setSearchTerm,
    loadAccountType,
    loadMore,
    error,
  } = treeState;

  useLayoutEffect(() => {
    if (!isOpen) return;
    const focusSearch = () => {
      searchInputRef.current?.focus({ preventScroll: true });
    };
    focusSearch();
    const timeoutId = window.setTimeout(focusSearch, 0);
    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen, setSearchTerm]);

  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen]);

  useEffect(() => {
    if (!isOpen || !searchActive || !hasMore || loading || loadingMore) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: "80px",
        threshold: 0.1,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, searchActive, hasMore, loading, loadingMore, loadMore]);

  const handleSelect = useCallback(
    (account: GLMappingEntity) => {
      onSelect(account);
      setIsOpen(false);
      setSearchTerm("");
    },
    [onSelect, setSearchTerm],
  );

  const handleClearSearch = () => {
    setSearchTerm("");
    searchInputRef.current?.focus();
  };

  const displayLabel = getAccountNameLeaf(selectedAccountName) || placeholder;

  const hasSelectedAccount = !searchActive && Boolean(selectedAccountId);

  const orderedGroups = useMemo(() => {
    const filtered = singleAccountType
      ? groups.filter((group) => group.account_type === singleAccountType)
      : groups;

    return orderGroupsWithSelectedFirst(
      filtered,
      selectedAccountType,
      searchActive,
    );
  }, [groups, singleAccountType, selectedAccountType, searchActive]);

  const selectedTreeNode = useMemo(() => {
    if (!hasSelectedAccount || !selectedAccountId) {
      return null;
    }
    return findNodeByAccountId(groups, selectedAccountId);
  }, [groups, hasSelectedAccount, selectedAccountId]);

  const pinnedSelectedName =
    selectedTreeNode?.gl_name ||
    getAccountNameLeaf(selectedAccountName) ||
    displayLabel;

  const pinnedAccountTypeLabel = useMemo(() => {
    const accountType =
      selectedTreeNode?.account_type || selectedAccountType || null;
    return accountType ? formatAccountTypeLabel(accountType) : null;
  }, [selectedTreeNode?.account_type, selectedAccountType]);

  const pinnedLocationPath = useMemo(() => {
    const treePath =
      hasSelectedAccount && selectedAccountId
        ? buildPathFromTree(groups, selectedAccountId)
        : null;
    const hierarchicalPath =
      treePath && treePath.includes(">")
        ? treePath
        : selectedTreeNode?.full_path && selectedTreeNode.full_path.includes(">")
          ? selectedTreeNode.full_path
          : treePath || selectedTreeNode?.full_path;

    return buildSelectedAccountDisplayPath(
      pinnedAccountTypeLabel,
      hierarchicalPath,
      pinnedSelectedName,
    );
  }, [
    groups,
    hasSelectedAccount,
    pinnedAccountTypeLabel,
    pinnedSelectedName,
    selectedAccountId,
    selectedTreeNode?.full_path,
  ]);

  const handlePinnedSelect = useCallback(
    (account: GLMappingEntity) => {
      if (selectedTreeNode) {
        handleSelect(treeNodeToGLMappingEntity(selectedTreeNode));
        return;
      }
      handleSelect(account);
    },
    [handleSelect, selectedTreeNode],
  );

  const hasGroups = searchActive
    ? groups.some((g) => g.nodes.length > 0)
    : groups.some((g) => g.total_leaves > 0);

  const panelWidthClass = dropdownClassName || "w-[640px]";

  return (
    <div className={`relative w-full ${className}`.trim()}>
      <SubframeCore.DropdownMenu.Root
        open={isOpen && !disabled}
        onOpenChange={(open) => {
          if (!disabled) {
            setIsOpen(open);
            onOpenChange?.(open);
          }
        }}
      >
        <SubframeCore.DropdownMenu.Trigger asChild={true}>
          <Button
            className={`w-full justify-between ${buttonClassName}`.trim()}
            variant="neutral-secondary"
            iconRight={<FeatherChevronDown />}
            disabled={disabled}
          >
            {displayLabel}
          </Button>
        </SubframeCore.DropdownMenu.Trigger>

        <SubframeCore.DropdownMenu.Portal>
          <SubframeCore.DropdownMenu.Content
            side="bottom"
            align="start"
            sideOffset={4}
            avoidCollisions={false}
            collisionPadding={16}
            asChild={true}
          >
            <div
              className={`flex max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] flex-col items-start rounded-md border border-solid border-neutral-border bg-default-background shadow-lg overflow-hidden searchable-dropdown-content ${panelWidthClass}`}
              data-searchable-dropdown-content="true"
            >
              <div className="relative flex w-full flex-none flex-col items-start gap-2 px-3 py-3 border-b border-solid border-neutral-border">
                <TextField
                  className="h-auto w-full flex-none"
                  variant="filled"
                  label=""
                  helpText=""
                  icon={<FeatherSearch />}
                >
                  <TextField.Input
                    ref={searchInputRef}
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="pr-10"
                  />
                </TextField>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-subtext-color hover:text-default-font"
                    aria-label="Clear search"
                  >
                    <FeatherX className="text-body font-body" />
                  </button>
                )}
              </div>

              <div
                ref={scrollContainerRef}
                className="flex min-h-0 flex-1 w-full flex-col items-start overflow-y-auto px-2 py-2"
              >
                {loading ? (
                  <div className="flex items-center justify-center py-8 w-full">
                    <Loader />
                  </div>
                ) : error ? (
                  <div className="px-3 py-4 text-body font-body text-subtext-color w-full">
                    {error}
                  </div>
                ) : !hasGroups ? (
                  <div className="px-3 py-4 text-body font-body text-subtext-color w-full">
                    {searchActive ? noResultsMessage : emptyMessage}
                  </div>
                ) : (
                  <>
                    {hasSelectedAccount && selectedAccountId && (
                      <CurrentlySelectedAccountRow
                        accountId={selectedAccountId}
                        accountName={pinnedSelectedName}
                        locationPath={pinnedLocationPath}
                        onSelect={handlePinnedSelect}
                      />
                    )}

                    {orderedGroups.map((group) => (
                      <AccountTypeGroupAccordion
                        key={group.account_type}
                        group={group}
                        searchActive={searchActive}
                        selectedAccountId={selectedAccountId}
                        isTypeLoading={loadingTypes.has(group.account_type)}
                        onExpandType={loadAccountType}
                        onSelect={handleSelect}
                        scrollContainerRef={scrollContainerRef}
                        selectionMode={selectionMode}
                        excludeAccountIds={excludeAccountIds}
                        defaultOpen={
                          autoExpandSingleGroup && orderedGroups.length === 1
                        }
                      />
                    ))}

                    {searchActive && loadingMore && (
                      <div className="flex items-center justify-center py-2 w-full">
                        <Loader />
                      </div>
                    )}

                    {searchActive && hasMore && !loadingMore && (
                      <div ref={sentinelRef} className="h-1 w-full" />
                    )}
                  </>
                )}
              </div>
            </div>
          </SubframeCore.DropdownMenu.Content>
        </SubframeCore.DropdownMenu.Portal>
      </SubframeCore.DropdownMenu.Root>
    </div>
  );
}

export type { UseBankGlTreeReturn };
